<template>
  <div data-test-id="order-table">
    <model-table
      ref="modelTable"
      :collection="orders"
      :columns="columns"
      :show-add="false"
      :show-edit="false"
      :edit-on-row-click="false"
      :grid="grid"
      :cell-class="props => classesForRow(props.row)"
      :submodel="submodel"
      @filter="prop => filter(prop)"
      @row-clicked="row => handleRowClick(row)"
    >
      <template
        #card="props"
      >
        <order-table-grid
          :props="props"
          @select-order="order => $emit('select-order', order)"
          @start-void="order => $emit('start-void', order)"
          @start-payment="order => $emit('start-payment', order)"
          @view-details="order => $refs.modelTable.edit(order)"
        />
      </template>
      <template
        v-if="showControls"
        #header-controls
      >
        <q-btn
          v-if="$register"
          icon="print"
          @click="$refs.printDialog.show()"
        />
        <div v-if="showingId">
          <q-btn
            class="download-link"
            icon="download"
            dense
            round
            @click.stop="exportShowing(showingId)"
          />
        </div>
        <order-table-filter
          ref="orderTableFilter"
          :initial-state="initialState"
          @owner-change="(value) => { employeeId = value }"
          @state-change="(value) => { state = value }"
          @type-change="(value) => { type = value }"
          @register-station-change="(value) => { registerStationId = (value && value.id) || null}"
          @delivery-location-change="(value) => { deliveryLocation = value }"
          @for-showings-change="(value) => { forShowings = value }"
          @sales-channel-change="(value) => { salesChannel = value }"
        />
        <div class="row q-mt-sm">
          <q-btn
            v-if="lookbackDays === 7"
            label="Last 7 Days"
            icon-right="clear"
            outline
            dense
            @click="lookbackDays = null; showCalendar = true"
          />
          <q-btn
            v-else-if="lookbackDays === 30"
            label="Last 30 Days"
            icon-right="clear"
            outline
            dense
            @click="lookbackDays = null; showCalendar = true"
          />
          <q-input
            v-else
            :model-value="(dateRange && dateRange.from && IsoDate.parse(dateRange.from).i18n('shortDate') || dateRange && IsoDate.parse(dateRange).i18n('shortDate') || '') + (dateRange && dateRange.to ? ' to ' : '') + (dateRange && dateRange.to && IsoDate.parse(dateRange.to).i18n('shortDate') || '')"
            style="min-width: 250px;"
            placeholder="Range"
            dense
            clearable
            @clear="dateRange = null"
            @click="showCalendar = true"
          >
            <template #append>
              <q-icon
                name="event"
                class="cursor-pointer"
              >
                <q-popup-proxy
                  v-model="showCalendar"
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date
                    v-model="dateRange"
                    mask="YYYY-MM-DD"
                    range
                    @update:model-value="showCalendar = false"
                  >
                    <div class="row items-center justify-center q-gutter-sm">
                      <q-btn
                        v-close-popup
                        label="Last 7 Days"
                        color="primary"
                        outline
                        dense
                        size="sm"
                        @click="setRelativeDateRange(7)"
                      />
                      <q-btn
                        v-close-popup
                        label="Last 30 Days"
                        color="primary"
                        outline
                        dense
                        size="sm"
                        @click="setRelativeDateRange(30)"
                      />
                      <q-btn
                        v-close-popup
                        label="All"
                        color="primary"
                        outline
                        dense
                        size="sm"
                        @click="setRelativeDateRange(null)"
                      />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>
        <div
          v-if="$isPermitted($permissions.Concession.View)"
          class="row q-mt-sm"
        >
          <q-btn-toggle
            v-model="showAllSites"
            size="md"
            class="q-my-xs"
            outline
            dense
            toggle-color="primary"
            options-label-position="left"
            options-label-align="center"
            :options="[
              { label: 'Circuit', value: true },
              { label: 'Site', value: false }
            ]"
          />
        </div>
      </template>

      <template
        v-if="showControls"
        #row-actions="props"
      >
        <div class="row q-gutter-sm justify-end no-wrap">
          <q-btn
            outline
            data-test-id="order-table-view-order-details-button"
            @click.stop="handleViewDetails(props.row)"
          >
            View Details
          </q-btn>
          <q-btn
            v-if="$register && (props.row.state === 'draft' || props.row.state === 'open_tab' || props.row.state === 'pending')"
            outline
            data-test-id="order-table-select-order-button"
            @click.stop="$emit('select-order', props.row)"
          >
            Select
          </q-btn>
        </div>
      </template>

      <order-form
        @select-order="order => $emit('select-order', order)"
        @request-authorization="callback => $emit('request-authorization', callback)"
        @force-update="() => $refs.modelTable.handleChange()"
      />
    </model-table>
    <q-dialog
      v-model="showAction"
      transition-show="fade-in"
      transition-hide="fade-out"
    >
      <q-card
        :style="{ width: '560px' }"
        class="q-mx-md"
      >
        <q-card-section class="row items-center q-gutter-md">
          <q-btn
            size="xl"
            class="col"
            color="secondary"
            @click="handleViewDetails(selectedOrder); showAction = false"
          >
            View Details
          </q-btn>
          <q-btn
            size="xl"
            class="col"
            color="primary"
            @click="$emit('select-order', selectedOrder); showAction = false"
          >
            Select Order
          </q-btn>
        </q-card-section>
      </q-card>
    </q-dialog>
    <print-dialog
      ref="printDialog"
      :order-count="orders.data ? orders.data.length : 0"
      @print="print"
    />
    <receipt-dialog
      ref="receiptDialog"
      :order-id="receiptOrderId"
    />
  </div>
</template>

<script>
import { date, Notify } from 'quasar'
import { serverUri } from 'src/config'
import { IsoDate } from 'src/models/model'
import ReceiptDialog from 'src/modules/admin/order/ReceiptDialog'

const { formatDate, addToDate, extractDate, getDateDiff } = date

import ModelTable from 'src/modules/admin/components/ModelTable'
import QueryEncodableOptionsMixin from 'src/modules/admin/mixins/QueryEncodableOptionsMixin'
import OrderItemsCell from 'src/modules/admin/order/components/OrderItemsCell'
import OrderStateCell from 'src/modules/admin/order/components/OrderStateCell'
import OrderTableFilter from 'src/modules/admin/order/components/OrderTableFilter'
import OrderTableGrid from 'src/modules/admin/order/components/OrderTableGrid'
import PrintDialog from 'src/modules/admin/order/components/PrintDialog'
import OrderForm from 'src/modules/admin/order/OrderForm'

export default {
  name: 'OrderTable',
  components: {
    ModelTable,
    OrderForm,
    OrderTableGrid,
    PrintDialog,
    OrderTableFilter,
    ReceiptDialog
  },
  mixins: [QueryEncodableOptionsMixin],
  context: ['currentRegisterClient', 'currentUser'],
  configuration: ['currentSite', 'sites'],
  models: {
    orders: {
      user: true,
      orderItems: {
        fields: ['id', 'type', 'description', 'state', 'price', 'computedPaidTotal', 'discountAmount', 'voucherAmount', 'taxable', 'taxAmount', 'collectedAt']
      },
      employee: {
        fields: ['id', 'firstName', 'lastName']
      },
      skipWatchers: true,
      variables () {
        return {
          employeeId: this.employeeId,
          userId: this.userId,
          state: this.state,
          type: this.type,
          rangeStart: this.dateRange?.from || this.dateRange,
          rangeEnd: this.dateRange?.to || this.dateRange,
          showingId: this.showingId,
          giftCardId: this.giftCardId,
          voucherId: this.voucherId,
          deliveryLocation: this.deliveryLocation,
          orderToExclude: this.orderToExclude,
          registerStationId: this.registerStationId,
          forShowings: this.forShowings,
          showAllSites: this.showAllSites,
          salesChannel: this.salesChannel,
          noCount: true
        }
      },
      force: true,
      immediate: false
    }
  },
  props: {
    userId: {
      type: Number,
      required: false,
      default: null
    },
    showingId: {
      type: Number,
      required: false,
      default: null
    },
    giftCardId: {
      type: Number,
      required: false,
      default: null
    },
    voucherId: {
      type: Number,
      required: false,
      default: null
    },
    initialState: {
      type: String,
      required: false,
      default: null
    },
    orderToExclude: {
      type: Number,
      required: false,
      default: null
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true
    },
    grid: {
      type: Boolean,
      required: false,
      default: false
    },
    submodel: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['select-order', 'start-void', 'start-payment', 'request-authorization'],
  data () {
    return {
      employeeId: null,
      state: this.initialState,
      type: null,
      deliveryLocation: null,
      selectedOrder: null,
      showAction: false,
      dateRange: null,
      showCalendar: false,
      lookbackDays: null,
      showAllDates: false,
      expandId: null,
      registerStationId: null,
      forShowings: null,
      salesChannel: null,
      showAllSites: false,
      receiptOrderId: null
    }
  },
  computed: {
    defaultQueryEncodableOptions () {
      const defaultOptions = {
        // from props
        userId: this.userId,
        showingId: this.showingId,
        giftCardId: this.giftCardId,
        voucherId: this.voucherId,
        orderToExclude: this.orderToExclude,
        // from initial data
        deliveryLocation: null,
        type: null,
        state: this.initialState,
        showAllDates: false,
        registerStationId: this.registerStationId
      }
      // computed
      const dateRange = this.getRelativeDateRange(this.getDefaultLookbackDays())
      if (_.isObject(dateRange)) {
        defaultOptions.rangeStart = dateRange.from
        defaultOptions.rangeEnd = dateRange.to
      } else if (dateRange) {
        defaultOptions.rangeStart = dateRange
        defaultOptions.rangeEnd = dateRange
      }
      return defaultOptions
    },
    currentQueryDecodedOptions () {
      const decodedOptions = {}
      _.each(this.$route.query, (value, key) => {
        if (_.isNumber(value) || _.isBoolean(value) || !_.isEmpty(value)) {
          const decodedValue = this.decodeUrlParamOption(key, value)
          if (decodedValue !== null && (key === 'rangeStart' || key === 'rangeEnd' || decodedValue !== this.defaultQueryEncodableOptions[key])) {
            decodedOptions[key] = decodedValue
          }
        }
      })
      return decodedOptions
    },
    currentQueryEncodableOptions () {
      let optionsToEncode = {
        // from props
        userId: this.userId,
        showingId: this.showingId,
        giftCardId: this.giftCardId,
        voucherId: this.voucherId,
        orderToExclude: this.orderToExclude,
        // from current data
        deliveryLocation: this.deliveryLocation,
        type: this.type,
        state: this.state,
        // computed
        rangeStart: this.dateRange?.from || this.dateRange,
        rangeEnd: this.dateRange?.to || this.dateRange,
        showAllSites: this.showAllSites,
        showAllDates: this.showAllDates,
        registerStationId: this.registerStationId
      }
      optionsToEncode = _.pickBy(optionsToEncode, (value) => _.isNumber(value) || _.isBoolean(value) || !_.isEmpty(value))
      optionsToEncode = _.omitBy(optionsToEncode, (value, key) => {
        return key !== 'rangeStart' && key !== 'rangeEnd' && value === this.defaultQueryEncodableOptions[key]
      })
      return optionsToEncode
    },
    columns () {
      const orderColumns = [
        { field: 'id' }
      ]
      if (this.showAllSites) {
        orderColumns.push({
          name: 'site',
          field: order => this.siteNameChipForOrder(order),
          component: 'chip'
        })
      }
      return orderColumns.concat([
        { name: 'state', component: OrderStateCell },
        { name: 'user', field: order => this.nameChipForOrder(order), component: 'chip', classes: 'sensitive-column' },
        { name: 'orderItems', component: OrderItemsCell, limitValues: 4 },
        { field: 'total', component: 'money' },
        { field: 'registerStation', component: 'model' },
        { label: 'Delivery', field: 'deliveryLocation', component: 'chip', filterEnabled: true, filter: this.deliveryLocation },
        { field: 'employee', component: 'model', classes: 'sensitive-column' },
        { label: 'Remote Application', field: 'remoteOrderApplicationName', component: 'chip' },
        { name: 'collected', field: order => !order.hasUncollectedItems, component: 'checkmark' },
        { field: 'updatedAt', component: 'date-time' },
        { name: 'actions', align: 'right' }
      ])
    }
  },
  watch: {
    'currentSite.siteDate': {
      handler () {
        if (!this.$route.query.rangeStart && String(this.$route.query.showAllDates) !== 'true') {
          this.setDefaultRange()
        }
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, {
      OrderStateCell,
      OrderItemsCell,
      IsoDate
    })
  },
  methods: {
    async print (printMerchantCopy) {
      await this.orders.printBulkOrdersReceipt(_.map(this.orders.data, 'id'), printMerchantCopy)

      this.$refs.printDialog.hide()
    },
    initializeQueryEncodableOptions () {
      this.$nextTick(() => {
        this.$refs.orderTableFilter.setFilters({
          state: this.currentQueryDecodedOptions.state,
          type: this.currentQueryDecodedOptions.type,
          registerStationId: this.currentQueryDecodedOptions.registerStationId,
          deliveryLocation: this.currentQueryDecodedOptions.deliveryLocation
        })
      })
      if (this.currentQueryDecodedOptions.rangeStart && this.currentQueryDecodedOptions.rangeEnd) {
        this.dateRange = { from: this.currentQueryDecodedOptions.rangeStart, to: this.currentQueryDecodedOptions.rangeEnd }
        if (this.dateRange.to === this.currentSite.siteDate) { // range ending today
          this.lookbackDays = getDateDiff(this.dateRange.to, this.dateRange.from)
        }
      } else if (this.currentQueryDecodedOptions.rangeStart) {
        this.dateRange = this.currentQueryDecodedOptions.rangeStart // as a string
        const dateDiff = getDateDiff(this.currentSite.siteDate, this.dateRange)
        if (dateDiff === 1) {
          this.lookbackDays = 1
        } else if (dateDiff === 0) {
          this.lookbackDays = 0
        }
      } else if (this.currentQueryDecodedOptions.showAllDates) {
        this.dateRange = null
        this.lookbackDays = null
        this.showAllDates = true
      }
    },
    decodeUrlParamOption (key, value) {
      switch (key) {
        case 'userId':
        case 'showingId':
        case 'giftCardId':
        case 'voucherId':
        case 'registerStationId':
        case 'orderToExclude':
          return parseInt(value) || undefined
        case 'state':
        case 'type':
        case 'deliveryLocation':
          return value || undefined
        case 'rangeStart':
        case 'rangeEnd':
          return IsoDate.parse(value).isDate() ? IsoDate.parse(value).format('YYYY-MM-DD') : undefined
        case 'showAllDates':
          return String(value) === 'true'
        default:
          return undefined
      }
    },
    classesForRow (order) {
      let classes = ''

      if (order.state === 'canceled') {
        classes += 'bg-red-1 text-strike'
      } else if (order.state === 'draft') {
        classes += 'bg-grey-3 text-grey-6'
      } else if (order.state === 'pending') {
        classes += 'bg-amber-2'
      } else if (order.state === 'open_tab') {
        classes += 'bg-green-1'
      } else if (order.state === 'partially_voided' || order.state === 'partially_refunded') {
        classes += 'bg-yellow-1'
      } else if (order.state === 'voided' || order.state === 'refunded') {
        classes += 'bg-red-1'
      }

      return classes
    },
    nameChipForOrder (order) {
      if (order.nameForOrder) {
        return {
          label: order.nameForOrder
        }
      }
      if (order.deliveryFirstName) {
        return {
          label: [order.deliveryFirstName, order.deliveryLastName].join(' ')
        }
      }
      if (order.user && !!order.user.name) {
        return {
          label: order.user.name,
          color: 'primary',
          textColor: 'white'
        }
      }

      if (order.deliveryEmailAddress) {
        return {
          label: order.deliveryEmailAddress,
          color: 'secondary',
          textColor: 'white'
        }
      }

      return null
    },
    siteNameChipForOrder (order) {
      if (order.siteId !== this.currentSite.id) {
        return {
          label: order.siteName,
          color: 'warning',
          textColor: 'white'
        }
      } else {
        return {
          label: order.siteName,
          color: 'primary',
          textColor: 'white'
        }
      }
    },
    filter (prop) {
      if (prop.name === 'deliveryLocation') {
        this.deliveryLocation = prop.value
      }
    },
    handleRowClick (order) {
      if (order.siteId !== this.currentSite.id) {
        this.receiptOrderId = order.id
        this.$refs.receiptDialog.show(order.id, order.siteId)
        return
      }
      if (this.$register) {
        this.$emit('select-order', order)
      } else {
        this.$refs.modelTable.edit(order)
      }
    },
    handleViewDetails (order) {
      if (order.siteId !== this.currentSite.id) {
        this.receiptOrderId = order.id
        this.$refs.receiptDialog.show(order.id, order.siteId)
        return
      }
      this.$refs.modelTable.edit(order)
    },
    exportShowing (showingId) {
      window.location.href = `${serverUri}/export_showing?site_id=${this.currentSite.id}&showing_id=${showingId}`

      Notify.create({
        message: 'Download Started',
        position: 'top',
        type: 'positive'
      })
    },
    setDefaultRange () {
      this.setRelativeDateRange(this.getDefaultLookbackDays())
    },
    setRelativeDateRange (lookbackDays) {
      this.lookbackDays = lookbackDays
      this.dateRange = this.getRelativeDateRange(lookbackDays)
      if (lookbackDays === null) {
        this.showAllDates = true
      } else {
        this.showAllDates = false
      }
    },
    getDefaultLookbackDays () {
      return this.showingId || this.voucherId || this.userId || this.giftCardId ? null : 7
    },
    getRelativeDateRange (lookbackDays) {
      const today = this.currentSite.siteDate
      if (lookbackDays === null) {
        return null
      } else if (lookbackDays === 0) {
        return today
      } else if (lookbackDays === 1) {
        return formatDate(addToDate(extractDate(today, 'YYYY-MM-DD'), { days: -lookbackDays }), 'YYYY-MM-DD')
      } else {
        return {
          from: formatDate(addToDate(extractDate(today, 'YYYY-MM-DD'), { days: -lookbackDays }), 'YYYY-MM-DD'),
          to: this.currentSite.siteDate
        }
      }
    }
  }
}
</script>
